import { Calendar, CheckCircle, CreditCard, XCircle } from "@phosphor-icons/react";
import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from "@reactive-resume/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";

import { PaymentModal } from "@/client/components/payment/payment-modal";
import { usePayment } from "@/client/hooks/use-payment";
import { useSubscription } from "@/client/hooks/use-subscription";
import { type PaymentHistoryResponse, paymentService } from "@/client/services/payment";

const getStatusBadgeVariant = (status: string) => {
  if (status === "SUCCESSFUL") return "success";
  if (status === "PENDING") return "warning";
  if (status === "CANCELLED") return "secondary";
  return "error";
};

export const SubscriptionPage = () => {
  const queryClient = useQueryClient();
  const { subscription, refresh } = useSubscription();
  const { pollPaymentStatus } = usePayment();
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentId = params.get("OrderMerchantReference");

    if (paymentId) {
      pollPaymentStatus(paymentId);

      void refresh();
      void queryClient.invalidateQueries({ queryKey: ["payment-history"] });

      params.delete("pesapal");
      params.delete("OrderTrackingId");
      params.delete("OrderMerchantReference");
      params.delete("OrderNotificationType");

      const next = params.toString();
      const newUrl = next ? `${window.location.pathname}?${next}` : window.location.pathname;
      window.history.replaceState({}, "", newUrl);
    }
  }, [pollPaymentStatus, queryClient, refresh]);

  const { data: paymentHistoryData } = useQuery<PaymentHistoryResponse>({
    queryKey: ["payment-history"],
    queryFn: paymentService.getPaymentHistory,
  });

  const paymentHistory = paymentHistoryData?.payments ?? [];

  const expiresAt =
    subscription.subscription?.expiresAt ??
    (subscription as { expiresAt?: string | null }).expiresAt;

  const daysRemaining =
    subscription.hasActiveSubscription && expiresAt
      ? Math.max(0, dayjs(expiresAt).diff(dayjs(), "day"))
      : null;

  const planType =
    subscription.planType ??
    (subscription.subscription as { planType?: string | null } | null)?.planType ??
    null;

  const hasOneTimePurchase = planType === "ONE_TIME_DOWNLOAD" || planType === "one_time";
  const showSubscribeCta = !subscription.hasActiveSubscription && !hasOneTimePurchase;

  const getBannerClass = () => {
    if (subscription.hasActiveSubscription) return "border-success bg-success/5";
    if (hasOneTimePurchase) return "border-info bg-info/5";
    return "border-warning bg-warning/5";
  };

  const getStatusIcon = () => {
    if (subscription.hasActiveSubscription) {
      return <CheckCircle className="size-8 text-success" weight="fill" />;
    }
    if (hasOneTimePurchase) {
      return <CheckCircle className="size-8 text-info" weight="fill" />;
    }
    return <XCircle className="size-8 text-warning" weight="fill" />;
  };

  const getStatusTitle = () => {
    if (subscription.hasActiveSubscription) return "Active Subscription";
    if (hasOneTimePurchase) return "One-Time Purchase";
    return "No Active Subscription";
  };

  return (
    <>
      <Helmet>
        <title>Subscription - VillageHut Resume</title>
      </Helmet>

      <div className="space-y-6">
        {/* Subscription Status Banner */}
        <Card className={getBannerClass()}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {getStatusIcon()}
                <div>
                  <h2 className="text-lg font-semibold">{getStatusTitle()}</h2>
                  {subscription.hasActiveSubscription && expiresAt && (
                    <p className="text-sm text-medium-gray">
                      Expires: {dayjs(expiresAt).format("MMMM D, YYYY")}
                      {daysRemaining !== null && ` (${daysRemaining} days remaining)`}
                    </p>
                  )}
                  {hasOneTimePurchase && !subscription.hasActiveSubscription && (
                    <p className="text-sm text-medium-gray">
                      Your purchase is active. You can download resumes within your one-time limit.
                    </p>
                  )}
                  {showSubscribeCta && (
                    <p className="text-sm text-medium-gray">
                      Subscribe to unlock unlimited downloads
                    </p>
                  )}
                </div>
              </div>
              {showSubscribeCta && (
                <Button
                  className="bg-accent text-accent-foreground hover:bg-accent-accent"
                  onClick={() => {
                    setShowPaymentModal(true);
                  }}
                >
                  Subscribe Now
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Quick Stats - Days remaining when subscription is active */}
        {subscription.hasActiveSubscription && (
          <div className="grid gap-4 md:grid-cols-1">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="flex size-12 items-center justify-center rounded-full bg-info/10">
                    <Calendar className="size-6 text-info" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{String(daysRemaining ?? "—")}</div>
                    <div className="text-sm text-medium-gray">Days Remaining</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Payment History */}
        <Card>
          <CardHeader>
            <CardTitle>Payment History</CardTitle>
          </CardHeader>
          <CardContent>
            {paymentHistory.length === 0 ? (
              <div className="py-8 text-center text-medium-gray">
                <CreditCard className="mx-auto mb-4 size-12 opacity-50" />
                <p>No payment history yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="px-4 py-3 text-left text-sm font-semibold">Date</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Amount</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentHistory
                      .filter(
                        (
                          p: {
                            id?: string;
                            createdAt?: string;
                            type?: string;
                            amount?: number;
                            status?: string;
                          } | null,
                        ): p is NonNullable<typeof p> => p?.id != null,
                      )
                      .map(
                        (payment: {
                          id: string;
                          createdAt: string;
                          type: string;
                          amount: number;
                          status: string;
                        }) => (
                          <tr key={payment.id} className="border-b">
                            <td className="px-4 py-3 text-sm">
                              {dayjs(payment.createdAt).format("MMM D, YYYY")}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {payment.type === "ONE_TIME_DOWNLOAD"
                                ? "One-Time Download"
                                : "Monthly Subscription"}
                            </td>
                            <td className="px-4 py-3 text-sm font-medium">KES {payment.amount}</td>
                            <td className="px-4 py-3">
                              <Badge variant={getStatusBadgeVariant(payment.status)}>
                                {payment.status}
                              </Badge>
                            </td>
                          </tr>
                        ),
                      )}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => {
          setShowPaymentModal(false);
        }}
        onSuccess={() => {
          void refresh();
          void queryClient.invalidateQueries({ queryKey: ["payment-history"] });
        }}
      />
    </>
  );
};
